/**
 * FeeScheduleUploadModal
 *
 * 3-step modal for uploading a fee schedule PDF and importing extracted rates.
 *
 * Step 1 – Upload:     Drag & drop a PDF → POST clinic-documents with extraction_tasks=fee_schedule
 * Step 2 – Processing: Poll /clinic-documents/{id}/status every 3s until metadata_ready
 * Step 3 – Import:     Poll /extraction-results/fee_schedule until completed, show preview, import
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useImportFeeScheduleFromExtractionMutation } from '@features/billing/store/api';
import type { FeeScheduleExtractedData } from '@features/billing/store/fee-schedule-documents-api';
import {
  useGetFeeScheduleDocumentStatusQuery,
  useGetFeeScheduleExtractionResultQuery,
  useUploadFeeScheduleDocumentMutation,
} from '@features/billing/store/fee-schedule-documents-api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

// ─── Types ────────────────────────────────────────────────────────────────────

type UploadStep = 'upload' | 'processing' | 'import';
type RatePurpose = 'standard_charge' | 'payer_expected_rate';

interface FeeScheduleUploadModalProps {
  open: boolean;
  ratePurpose: RatePurpose;
  payerId?: string;
  payerName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── DropZone ─────────────────────────────────────────────────────────────────

interface DropZoneProps {
  file: File | null;
  isDragging: boolean;
  isLoading: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}

function DropZone({
  file,
  isDragging,
  isLoading,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileChange,
  onClear,
}: DropZoneProps) {
  if (file) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          border: '1px solid',
          borderColor: 'success.light',
          borderRadius: 1,
          backgroundColor: 'success.50',
        }}
      >
        <InsertDriveFileIcon color="success" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(file.size / 1024).toFixed(1)} KB
          </Typography>
        </Box>
        {!isLoading && (
          <IconButton size="small" onClick={onClear} aria-label="Remove file">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => document.getElementById('fee-schedule-file-input')?.click()}
      sx={{
        border: '2px dashed',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 4,
        textAlign: 'center',
        backgroundColor: isDragging ? 'action.hover' : 'background.default',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
      }}
    >
      <input
        id="fee-schedule-file-input"
        type="file"
        accept=".pdf"
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
      <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        Drag &amp; drop your fee schedule PDF
      </Typography>
      <Typography variant="caption" color="text.secondary">
        or click to select — PDF files only
      </Typography>
    </Box>
  );
}

// ─── Document status poller ───────────────────────────────────────────────────

function DocumentStatusPoller({
  clinicDocumentId,
  onReady,
}: {
  clinicDocumentId: string;
  onReady: () => void;
}) {
  const { data } = useGetFeeScheduleDocumentStatusQuery(
    { clinicDocumentId },
    { pollingInterval: 3000 },
  );

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (data?.processing_status === 'metadata_ready') {
      onReadyRef.current();
    }
  }, [data?.processing_status]);

  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <CircularProgress size={40} sx={{ mb: 2 }} />
      <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
        Uploading and processing your document…
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This typically takes 30–60 seconds.
      </Typography>
    </Box>
  );
}

// ─── Extraction preview panel ─────────────────────────────────────────────────

function ExtractionPreview({
  clinicDocumentId,
  effectiveDate,
  ratePurpose,
  payerName,
  onEffectiveDateChange,
  onReady,
}: {
  clinicDocumentId: string;
  effectiveDate: string;
  ratePurpose: RatePurpose;
  payerName?: string;
  onEffectiveDateChange: (v: string) => void;
  onReady: (extractionResultId: string, data: FeeScheduleExtractedData) => void;
}) {
  const [isStillPending, setIsStillPending] = useState(true);
  const { data, isError } = useGetFeeScheduleExtractionResultQuery(
    { clinicDocumentId, taskName: 'fee_schedule' },
    { pollingInterval: isStillPending ? 3000 : 0 },
  );

  useEffect(() => {
    if (data?.status && data.status !== 'pending') {
      setIsStillPending(false);
    }
  }, [data?.status]);

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (data?.status === 'completed' && data.extraction_result_id) {
      const extracted = (data.extracted_data ?? {}) as FeeScheduleExtractedData;
      onReadyRef.current(data.extraction_result_id, extracted);
    }
  }, [data?.status, data?.extraction_result_id, data?.extracted_data]);

  // Still running
  if (!data || data.status === 'pending') {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CircularProgress size={36} sx={{ mb: 2 }} />
        <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
          Extracting fee schedule data…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The AI is reading your document. This may take up to a minute.
        </Typography>
      </Box>
    );
  }

  // Failed
  if (data.status === 'failed' || isError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 2 }}>
        <ErrorOutlineIcon color="error" sx={{ mt: 0.25 }} />
        <Box>
          <Typography variant="body1" color="error" sx={{ fontWeight: 500 }}>
            Extraction failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {data.error_text ?? 'The AI was unable to parse this document. Please check that it is a valid fee schedule PDF and try again.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Completed — show preview
  const extracted = (data.extracted_data ?? {}) as FeeScheduleExtractedData;
  const rows = extracted.rows ?? [];
  const previewRows = rows.slice(0, 5);
  const amountLabel = ratePurpose === 'standard_charge' ? 'Claim charge' : 'Expected allowed';

  return (
    <Box>
      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip
          label={ratePurpose === 'standard_charge' ? 'Standard charges' : `Expected rates${payerName ? `: ${payerName}` : ''}`}
          size="small"
          color="primary"
          variant="outlined"
        />
        {extracted.payer_name && (
          <Chip label={`Payer: ${extracted.payer_name}`} size="small" variant="outlined" />
        )}
        {extracted.effective_date && (
          <Chip label={`Effective: ${extracted.effective_date}`} size="small" variant="outlined" />
        )}
        <Chip label={`${rows.length} billing code${rows.length !== 1 ? 's' : ''} found`} size="small" color="primary" variant="outlined" />
        {extracted.confidence !== undefined && (
          <Chip
            label={`Confidence: ${Math.round(extracted.confidence * 100)}%`}
            size="small"
            color={extracted.confidence >= 0.8 ? 'success' : 'warning'}
            variant="outlined"
          />
        )}
      </Box>

      {/* Row preview table */}
      {previewRows.length > 0 && (
        <>
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell align="right">{amountLabel}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map(row => (
                <TableRow key={row.code_id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {row.code_id.replace(/^CPT:|^HCPCS:/, '')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.code_id.startsWith('HCPCS:') ? 'HCPCS' : 'CPT'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    ${(row.charge_cents / 100).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rows.length > 5 && (
            <Typography variant="caption" color="text.secondary">
              + {rows.length - 5} more rows — all will be imported.
            </Typography>
          )}
        </>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Effective date override */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Effective date override (leave blank to use extracted date):
      </Typography>
      <TextField
        label="Effective Date (optional)"
        type="date"
        value={effectiveDate}
        onChange={e => onEffectiveDateChange(e.target.value)}
        size="small"
        fullWidth
       slotProps={{ inputLabel: { shrink: true } }}/>
    </Box>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function FeeScheduleUploadModal({
  open,
  ratePurpose,
  payerId,
  payerName,
  onClose,
  onSuccess,
}: FeeScheduleUploadModalProps) {
  const [step, setStep] = useState<UploadStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [clinicDocumentId, setClinicDocumentId] = useState<string | null>(null);
  const [extractionResultId, setExtractionResultId] = useState<string | null>(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [extractionFailed, setExtractionFailed] = useState(false);

  const [uploadDocument, { isLoading: isUploading }] = useUploadFeeScheduleDocumentMutation();
  const [importFeeSchedule, { isLoading: isImporting }] =
    useImportFeeScheduleFromExtractionMutation();

  const handleClose = useCallback(() => {
    if (isUploading || isImporting) return;
    setStep('upload');
    setFile(null);
    setClinicDocumentId(null);
    setExtractionResultId(null);
    setEffectiveDate('');
    setError(null);
    setImportSuccess(false);
    setExtractionFailed(false);
    onClose();
  }, [isUploading, isImporting, onClose]);

  // ── Drag & drop ───────────────────────────────────────────────────────────

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.type === 'application/pdf') {
      setFile(dropped);
      setError(null);
    } else {
      setError('Only PDF files are accepted.');
    }
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected?.type === 'application/pdf') {
      setFile(selected);
      setError(null);
    } else if (selected) {
      setError('Only PDF files are accepted.');
    }
    e.target.value = '';
  }, []);

  // ── Upload ────────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!file) return;
    setError(null);
    try {
      const result = await uploadDocument({ file }).unwrap();
      const docId = result.documents[0]?.clinic_document_id;
      if (!docId) throw new Error('No document ID returned from upload.');
      setClinicDocumentId(docId);
      setStep('processing');
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string }; message?: string };
      setError(apiErr?.data?.message ?? apiErr?.message ?? 'Upload failed. Please try again.');
    }
  };

  // ── Called when extraction result is ready ────────────────────────────────

  const handleExtractionReady = useCallback(
    (resultId: string, extracted: FeeScheduleExtractedData) => {
      setExtractionResultId(resultId);
      if (extracted.effective_date) {
        setEffectiveDate(extracted.effective_date);
      }
    },
    [],
  );

  // ── Import ────────────────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!extractionResultId) return;
    setError(null);
    try {
      await importFeeSchedule({
        feeScheduleImportRequest: {
          extraction_result_id: extractionResultId,
          rate_purpose: ratePurpose,
          ...(ratePurpose === 'payer_expected_rate' && payerId ? { payer_id: payerId } : {}),
          ...(effectiveDate ? { effective_date: effectiveDate } : {}),
        },
      }).unwrap();
      setImportSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string }; message?: string };
      setError(apiErr?.data?.message ?? apiErr?.message ?? 'Import failed. Please try again.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const titleByStep: Record<UploadStep, string> = {
    upload: ratePurpose === 'standard_charge' ? 'Upload Standard Charges' : 'Upload Payer Expected Rates',
    processing: 'Processing Document',
    import: 'Review & Import',
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {titleByStep[step]}
        <IconButton
          onClick={handleClose}
          disabled={isUploading || isImporting}
          size="small"
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* ── Step 1: Upload ──────────────────────────────────────────── */}
        {step === 'upload' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {ratePurpose === 'standard_charge'
                ? 'Upload a standard fee schedule PDF. Extracted amounts will become global claim charges.'
                : `Upload a payer contract or fee schedule PDF. Extracted amounts will become expected allowed rates${payerName ? ` for ${payerName}` : ''}.`}
            </Typography>
            {ratePurpose === 'payer_expected_rate' && !payerId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Select a payer before importing expected rates.
              </Alert>
            )}
            <DropZone
              file={file}
              isDragging={isDragging}
              isLoading={isUploading}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileChange={handleFileChange}
              onClear={() => setFile(null)}
            />
            {isUploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Uploading…
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* ── Step 2: Processing (document indexing) ──────────────────── */}
        {step === 'processing' && clinicDocumentId && (
          <DocumentStatusPoller
            clinicDocumentId={clinicDocumentId}
            onReady={() => setStep('import')}
          />
        )}

        {/* ── Step 3: Import (extraction result + preview) ────────────── */}
        {step === 'import' && clinicDocumentId && (
          <>
            {importSuccess ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Fee schedule imported successfully!
                </Typography>
              </Box>
            ) : (
              <ExtractionPreview
                clinicDocumentId={clinicDocumentId}
                effectiveDate={effectiveDate}
                ratePurpose={ratePurpose}
                payerName={payerName}
                onEffectiveDateChange={setEffectiveDate}
                onReady={handleExtractionReady}
              />
            )}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isUploading || isImporting}>
          {importSuccess ? 'Close' : 'Cancel'}
        </Button>

        {step === 'upload' && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!file || isUploading || (ratePurpose === 'payer_expected_rate' && !payerId)}
          >
            {isUploading ? 'Uploading…' : 'Upload & Extract'}
          </Button>
        )}

        {step === 'import' && !importSuccess && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!extractionResultId || isImporting || extractionFailed || (ratePurpose === 'payer_expected_rate' && !payerId)}
          >
            {isImporting ? 'Importing…' : ratePurpose === 'standard_charge' ? 'Import standard charges' : 'Import expected rates'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
